import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../utils/formatters';

export const GlassModal = ({
  isOpen,
  onClose,
  children,
  className,
  size = 'md',
  title,
  description,
  icon: Icon,
  footer,
  closeOnOverlayClick = true,
  showCloseButton = true,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-[95vw]',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeOnOverlayClick ? onClose : undefined}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            className={cn(
              'relative w-full',
              'bg-slate-900/90 backdrop-blur-2xl',
              'border border-white/10 rounded-2xl',
              'shadow-glass-lg',
              sizes[size],
              className
            )}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  {Icon && (
                    <div className="p-2 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20">
                      <Icon className="w-5 h-5 text-accent-primary" />
                    </div>
                  )}
                  <div>
                    {title && (
                      <h3 className="text-lg font-semibold text-white/90">{title}</h3>
                    )}
                    {description && (
                      <p className="text-sm text-white/50">{description}</p>
                    )}
                  </div>
                </div>
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-white/50 hover:text-white/90 hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 bg-white/[0.02] rounded-b-2xl">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export const GlassDrawer = ({
  isOpen,
  onClose,
  children,
  className,
  position = 'right',
  size = 'md',
  title,
  showCloseButton = true,
}) => {
  const sizes = {
    sm: 'w-80',
    md: 'w-96',
    lg: 'w-[32rem]',
    xl: 'w-[40rem]',
    full: 'w-full',
  };

  // Side drawers use the entire phone viewport, avoiding a clipped column and
  // keeping the ERP bottom navigation behind the active task.
  const mobileSizes = {
    sm: 'w-full sm:w-80',
    md: 'w-full sm:w-96',
    lg: 'w-full sm:w-[32rem]',
    xl: 'w-full sm:w-[40rem]',
    full: 'w-full',
  };

  const positions = {
    left: 'left-0 inset-y-0',
    right: 'right-0 inset-y-0',
    top: 'top-0 w-full',
    bottom: 'bottom-0 w-full',
  };

  const animations = {
    left: { x: '-100%', opacity: 0 },
    right: { x: '100%', opacity: 0 },
    top: { y: '-100%', opacity: 0 },
    bottom: { y: '100%', opacity: 0 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[90]">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={animations[position]}
            animate={{ x: 0, y: 0, opacity: 1 }}
            exit={animations[position]}
            transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            className={cn(
              'absolute',
              'bg-slate-900/95 backdrop-blur-2xl',
              'border border-white/10',
              'shadow-glass-lg',
              positions[position],
              ['left', 'right'].includes(position) && mobileSizes[size],
              ['top', 'bottom'].includes(position) && 'h-96',
              className
            )}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className="flex items-center justify-between px-4 py-3.5 sm:px-6 sm:py-4 border-b border-white/10">
                {title && <h3 className="text-lg font-semibold text-white/90">{title}</h3>}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-white/50 hover:text-white/90 hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(100dvh-64px)] sm:max-h-[calc(100vh-80px)]">
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GlassModal;
